import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, Send } from "lucide-react";
import { SITE, SOLUTIONS } from "@/config/site";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid business email"),
  company: z.string().min(1, "Please enter your company name"),
  phone: z.string().max(40).optional().or(z.literal("")),
  solution: z.string().min(1, "Please choose a solution"),
  message: z.string().min(10, "Tell us a little more — at least 10 characters"),
});

const inputCls =
  "w-full rounded-md border border-silver bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink/40 transition-colors focus:border-cobalt focus:outline-none focus:ring-2 focus:ring-cobalt/20 aria-[invalid=true]:border-red-500";

export default function ContactForm({ compact = false }) {
  const [params] = useSearchParams();
  const pre = params.get("solution");
  const preName = SOLUTIONS.find((s) => s.slug === pre)?.name || "Not sure yet";
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { solution: preName },
  });

  const onSubmit = async (values) => {
    setServerError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/inquiries`,
        values
      );
      if (res.data && res.data.status === "success") {
        setDone(true);
      } else {
        throw new Error("Unexpected response");
      }
    } catch (e) {
      setServerError(
        "We couldn't submit your inquiry just now. Please try again, or call us at " +
          SITE.phone +
          "."
      );
    }
  };

  if (done) {
    return (
      <div
        className="rounded-md border border-silver/60 bg-white p-8 text-center shadow-sm"
        data-testid="contact-form-success"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-cobalt" />
        <h3 className="mt-4 font-display text-xl font-bold text-ink">Thank you — we're on it.</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/70">
          Your inquiry is with DJC Global. We'll review what you shared and connect you with the
          relevant specialist — with context on what to ask.
        </p>
        <button
          type="button"
          data-testid="contact-form-reset"
          onClick={() => {
            setDone(false);
            reset();
          }}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-silver bg-white px-5 py-2.5 font-display text-sm font-semibold text-ink transition-colors hover:border-cobalt hover:text-cobalt"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-testid="contact-form"
      className={`rounded-md border border-silver/60 bg-white shadow-sm ${
        compact ? "p-5 sm:p-6" : "p-6 sm:p-8"
      }`}
    >
      <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-sm font-semibold text-ink">
            Name <span className="text-cobalt">*</span>
          </label>
          <input
            id="cf-name"
            type="text"
            autoComplete="name"
            placeholder="Your name"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "cf-name-error" : undefined}
            data-testid="contact-name-input"
            className={inputCls}
            {...register("name")}
          />
          {errors.name && (
            <p id="cf-name-error" role="alert" data-testid="contact-name-error" className="mt-1.5 text-xs font-medium text-red-600">
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-sm font-semibold text-ink">
            Business email <span className="text-cobalt">*</span>
          </label>
          <input
            id="cf-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "cf-email-error" : undefined}
            data-testid="contact-email-input"
            className={inputCls}
            {...register("email")}
          />
          {errors.email && (
            <p id="cf-email-error" role="alert" data-testid="contact-email-error" className="mt-1.5 text-xs font-medium text-red-600">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cf-company" className="mb-1.5 block text-sm font-semibold text-ink">
            Company <span className="text-cobalt">*</span>
          </label>
          <input
            id="cf-company"
            type="text"
            autoComplete="organization"
            placeholder="Company name"
            aria-invalid={!!errors.company}
            aria-describedby={errors.company ? "cf-company-error" : undefined}
            data-testid="contact-company-input"
            className={inputCls}
            {...register("company")}
          />
          {errors.company && (
            <p id="cf-company-error" role="alert" data-testid="contact-company-error" className="mt-1.5 text-xs font-medium text-red-600">
              {errors.company.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cf-phone" className="mb-1.5 block text-sm font-semibold text-ink">
            Phone <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <input
            id="cf-phone"
            type="tel"
            autoComplete="tel"
            placeholder="Where we can reach you"
            data-testid="contact-phone-input"
            className={inputCls}
            {...register("phone")}
          />
        </div>

        <div className={compact ? "sm:col-span-2" : "sm:col-span-2"}>
          <label htmlFor="cf-solution" className="mb-1.5 block text-sm font-semibold text-ink">
            Solution of interest <span className="text-cobalt">*</span>
          </label>
          <select
            id="cf-solution"
            data-testid="contact-solution-select"
            className={`${inputCls} appearance-none`}
            {...register("solution")}
          >
            <option value="Not sure yet">Not sure yet — help me find a fit</option>
            {SOLUTIONS.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className={compact ? "sm:col-span-2" : "sm:col-span-2"}>
          <label htmlFor="cf-message" className="mb-1.5 block text-sm font-semibold text-ink">
            What would you like to improve? <span className="text-cobalt">*</span>
          </label>
          <textarea
            id="cf-message"
            rows={compact ? 3 : 4}
            placeholder="A sentence or two is plenty — costs, benefits, capacity, growth…"
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? "cf-message-error" : undefined}
            data-testid="contact-message-input"
            className={`${inputCls} resize-y`}
            {...register("message")}
          />
          {errors.message && (
            <p id="cf-message-error" role="alert" data-testid="contact-message-error" className="mt-1.5 text-xs font-medium text-red-600">
              {errors.message.message}
            </p>
          )}
        </div>
      </div>

      {serverError && (
        <div
          role="alert"
          data-testid="contact-form-error"
          className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-form-submit-button"
        className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cobalt px-7 py-3.5 font-display text-sm font-semibold text-white shadow-sm transition-colors hover:bg-midnight disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {isSubmitting ? (
          <>
            Sending
            <Loader2 className="h-4 w-4 animate-spin" />
          </>
        ) : (
          <>
            Send Inquiry
            <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </button>

      <p className="mt-4 text-xs leading-relaxed text-ink/55">
        Please don't include patient information, financial account details, or sensitive
        documents. Prefer to browse first?{" "}
        <Link to="/solutions" data-testid="contact-form-solutions-link" className="font-semibold text-cobalt hover:text-midnight">
          Explore the solutions
          <ArrowRight className="inline h-3 w-3" />
        </Link>
      </p>
    </form>
  );
}
